import { useState, useEffect } from 'react';
import ModalContactInfo from './ModalContactInfo';
import Spinner from '../../Spinner';
import { useApi } from '../../../api/ApiProvider';
import Api from '../../../axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { PermissionsGate } from '../../authorization/PermissionsGate';
import PhoneIcon from '@mui/icons-material/Phone';
import AddIcon from '@mui/icons-material/Add';
import intl from 'components/i18n/ReactIntlWrapper';
import { handleAPIError } from '../../../common/errorHandler';
import { Box, Button, Typography } from '@mui/material';
import { useDialog } from 'common/DialogProvider';
import { DEFAULT_PAGINATION, Sort, useSearch } from 'hooks/useSearch';
import { DataGrid, GridColDef, GridInitialState, GridSortModel } from '@mui/x-data-grid';
import { ContactInfo } from 'axiosApi/models';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { getUserEntity } from 'common/UserEntityProvider';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

const ContactInfoCompact = ({
  contactInfosId,
  editScopes,
  deleteScopes,
  setContactInfosId,
  disabled,
}) => {
  const api: Api = useApi();

  const [contactInfos, setContactInfos] = useState([]);
  const entityType = getUserEntity();

  const toast = useToastMessageQueue();

  const [loading, setLoading] = useState(false);
  const [isDeletingLoading, setIsDeletingLoading] = useState(false);
  const [isContactInfoDetailOpen, setIsContactInfoDetailOpen] = useState(false);
  const [contactInfoId, setContactInfoId] = useState(null);
  const [pagination, setPagination] = usePaginationPreference('contactInfoCompact', 10);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'contactName', sort: 'asc' },
  ]);

  const [errors, setErrors] = useState({});

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const fetchContactInfo = async (contactInfoIds: number[]) => {
    setLoading(true);
    try {
      const response = await api.contactinfoApi.apiVversionContactInfoContactinfosGet(
        '1',
        contactInfoIds,
        {}
      );

      if (response?.data?.data) {
        setContactInfos(response.data.data);
      } else {
        setContactInfos([]);
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contactInfosId.length > 0) {
      fetchContactInfo(contactInfosId);
    }
  }, [contactInfosId]);

  const handleEdit = (selectedContactInfoId) => {
    setContactInfoId(selectedContactInfoId);
    setIsContactInfoDetailOpen(true);
  };

  const { openDialog, closeDialog } = useDialog();

  const handleDelete = (selectedContactInfoId) => {
    const id = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.contactInfoRow.message'),
      loading: isDeletingLoading,
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          setLoading(true);
          try {
            const response = await api.contactinfoApi
              .apiVversionContactInfoContactinfoIdDelete(selectedContactInfoId, '1', {})
              .then((response) => {
                console.log(response.data);
                let newContactInfos = contactInfos.filter(
                  (contact) => contact.id != selectedContactInfoId
                );
                setContactInfos(newContactInfos);
                // Update contactInfosId array
                let newContactInfosId = contactInfosId.filter(
                  (id) => id != selectedContactInfoId
                );
                setContactInfosId(newContactInfosId);
                toast.success({
                  message: intl.get('contactInfoRow.toast.success.handleDelete'),
                });
                closeDialog(id);
              })
              .catch((error) => {
                handleAPIError(error, toast, errors);
                setErrors({ ...errors });
                closeDialog(id); // Close dialog even on error
              });
          } catch (error) {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
            closeDialog(id); // Close dialog on unexpected error
          } finally {
            setLoading(false);
          }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => {
          closeDialog(id);
        },
      },
    });
  };

  const closeContactInfoDetail = () => setIsContactInfoDetailOpen(false);

  const handleContactInfoDetailOpen = () => {
    setIsContactInfoDetailOpen(true);
  };

  const INITIAL_STATE: GridInitialState = {
    pagination: {
      paginationModel: {
        page: DEFAULT_PAGINATION.pageNum,
        pageSize: DEFAULT_PAGINATION.pageSize,
      },
    },
    sorting: {
      sortModel: [
        {
          field: 'Name',
          sort: 'asc',
        },
      ],
    },
  };

  const columns: GridColDef[] = [
    {
      field: 'contactName',
      headerName: intl.get('contactInfoCompact.table.thead.name'),
      width: 250,
      flex: 1,
    },
    {
      field: 'emailAddress',
      headerName: intl.get('contactInfoCompact.table.thead.email'),
      width: 250,
      flex: 1,
    },
    {
      field: 'phone1',
      headerName: intl.get('contactInfoCompact.table.thead.phone1'),
      width: 250,
      flex: 1,
    },
    {
      field: 'phone2',
      headerName: intl.get('contactInfoCompact.table.thead.phone2'),
      width: 250,
      flex: 1,
    },
    {
      field: 'role',
      headerName: intl.get('contactInfoCompact.table.thead.role'),
      width: 250,
      flex: 1,
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <DefaultActions
            row={params.row}
            entityType={entityType.entityType}
            onEdit={() => handleEdit(params.row.id)}
            editScopes={editScopes}
            disabled={disabled || loading}
          />
          <DefaultActions
            row={params.row}
            entityType={entityType.entityType}
            onDelete={() => handleDelete(params.row.id)}
            deleteScopes={deleteScopes}
            disabled={disabled || loading}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box className="card-header">
        <Typography
          variant="h6"
          className="title"
          display="flex"
          alignItems="center"
          color="primary"
          gap={1}
        >
          <PhoneIcon />
          {intl.get('contactInfoCompact.header')}
        </Typography>
      </Box>

      {loading ? (
        <Box
          style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Spinner small={true} />
        </Box>
      ) : (
        <Box className="card-body">
          {contactInfos && contactInfos.length > 0 ? (
            <Box style={{ width: '100%', display: 'flex' }}>
              <DataGrid
                rows={contactInfos || []}
                columns={columns}
                loading={loading}
                initialState={INITIAL_STATE}
                rowCount={contactInfos?.length}
                paginationModel={pagination}
                onPaginationModelChange={setPagination}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                pageSizeOptions={[5, 10, 20]}
              />
            </Box>
          ) : (
            <Box style={{ padding: '20px' }}></Box>
          )}

          <PermissionsGate
            viewScopes={editScopes}
            editScopes={editScopes}
            viewRoles={[]}
            editRoles={[]}
          >
            <Box textAlign="center" mt={2}>
              <Button
                variant="contained"
                color="primary"
                disabled={disabled}
                onClick={handleContactInfoDetailOpen}
                startIcon={<AddIcon />}
              >
                {intl.get('contactInfoCompact.newContact.button')}
              </Button>
              <ModalContactInfo
                isOpen={isContactInfoDetailOpen}
                closeModal={closeContactInfoDetail}
                contactInfoId={contactInfoId}
                contactInfos={contactInfos}
                setContactInfos={setContactInfos}
                contactInfosId={contactInfosId}
                setContactInfosId={setContactInfosId}
              />
            </Box>
          </PermissionsGate>
        </Box>
      )}
    </Box>
  );
};

export default ContactInfoCompact;
